/**
 * Mockup UI scale — same ratios as mockup-scale.css (--scale-side / --scale-play / --scale-ui).
 * Baseline: ui-w 720px → side column 120px, play column 480px.
 */

export const MOCKUP_UI_BASE_WIDTH_PX = 720;
export const MOCKUP_SIDE_BASE_WIDTH_PX = 120;
export const MOCKUP_PLAY_BASE_WIDTH_PX = 480;
/** Matches mockup-scale.css `--s-value` baseline (18px at scale-side 1). */
export const MOCKUP_SCORE_VALUE_BASE_PX = 18;
export const MOCKUP_SUN_DIAMETER_PX = 120;
// Moon sits between the old small disc (72) and near-sun size (114).
export const MOCKUP_MOON_DIAMETER_PX = 93;

/** Title / evolution disc width cap in horizontal side chrome (× grid unit U). */
export const CHROME_PANEL_MAX_UNIT_MULTIPLIER = 1.2;

/** Baseline side padding (--s-pad-x) for chrome panel width budget. */
export const CHROME_PANEL_SIDE_PAD_BASE_PX = 8;

/** Baseline button-row width (--s-chrome-content-w floor). */
export const CHROME_PANEL_BTN_ROW_BTN_BASE_PX = 48;
export const CHROME_PANEL_BTN_ROW_GAP_BASE_PX = 10;

/**
 * Title panel + evolution disc width in horizontal side chrome.
 * Grows with expanded side strips, capped at {@link CHROME_PANEL_MAX_UNIT_MULTIPLIER}×U.
 * @param {{ gridUnitPx?: number, sideStripWidthPx?: number, scaleSide?: number }} opts
 */
export function computeChromePanelWidthPx(opts) {
  opts = opts || {};
  var unit = Math.max(1, Math.floor(Number(opts.gridUnitPx) || MOCKUP_SIDE_BASE_WIDTH_PX));
  var stripW = Math.max(0, Math.floor(Number(opts.sideStripWidthPx) || unit));
  var scaleSide = Number(opts.scaleSide);
  if (!isFinite(scaleSide) || scaleSide <= 0) {
    scaleSide = unit / MOCKUP_SIDE_BASE_WIDTH_PX;
  }
  var minW =
    scaleMockupSidePx(CHROME_PANEL_BTN_ROW_BTN_BASE_PX, scaleSide) * 2 +
    scaleMockupSidePx(CHROME_PANEL_BTN_ROW_GAP_BASE_PX, scaleSide);
  var padX = scaleMockupSidePx(CHROME_PANEL_SIDE_PAD_BASE_PX, scaleSide);
  var available = Math.max(0, stripW - padX * 2);
  var maxW = Math.floor(unit * CHROME_PANEL_MAX_UNIT_MULTIPLIER);
  return Math.min(maxW, Math.max(minW, available));
}

/**
 * @param {number} uiWidthPx Grid / coreRect width in px.
 */
export function computeMockupUiScale(uiWidthPx) {
  var uiW = Math.max(1, Math.floor(Number(uiWidthPx) || 0));
  var colSide = uiW / 6;
  var colPlay = uiW * 4 / 6;
  return {
    uiWidth: uiW,
    colSideWidth: colSide,
    colPlayWidth: colPlay,
    scaleSide: colSide / MOCKUP_SIDE_BASE_WIDTH_PX,
    scalePlay: colPlay / MOCKUP_PLAY_BASE_WIDTH_PX,
    scaleUi: uiW / MOCKUP_UI_BASE_WIDTH_PX,
  };
}

/**
 * @param {number} basePx
 * @param {number} scaleSide
 */
export function scaleMockupSidePx(basePx, scaleSide) {
  return Math.max(0, Math.round(Number(basePx) * Number(scaleSide)));
}

/**
 * @param {number} basePx
 * @param {number} scalePlay
 */
export function scaleMockupPlayPx(basePx, scalePlay) {
  return Math.max(0, Math.round(Number(basePx) * Number(scalePlay)));
}

/**
 * Score value font size in px (`--s-value`), matching `.mockup-score-block__value`.
 * @param {number} scaleSide
 */
export function mockupScoreValuePx(scaleSide) {
  return scaleMockupSidePx(MOCKUP_SCORE_VALUE_BASE_PX, scaleSide);
}

/**
 * Live mockup `--scale-side` from CSS (same factor as score chrome).
 * @returns {number|null}
 */
export function readMockupScaleSideFromDom() {
  if (typeof document === "undefined" || typeof window.getComputedStyle !== "function") {
    return null;
  }
  var root = document.documentElement;
  if (!root) return null;
  var raw = window.getComputedStyle(root).getPropertyValue("--scale-side").trim();
  var n = parseFloat(raw);
  if (!isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * Fall countdown peak font-size = basePxAtScaleOne × scale-side.
 * @param {number} scaleSide
 * @param {number} [basePxAtScaleOne]
 */
export function fallCountdownFontSizePx(scaleSide, basePxAtScaleOne) {
  var side = Number(scaleSide);
  if (!isFinite(side) || side <= 0) side = 1;
  var base = basePxAtScaleOne != null && isFinite(basePxAtScaleOne) ? Number(basePxAtScaleOne) : 72;
  if (base <= 0) base = 72;
  return scaleMockupSidePx(base, side);
}

/**
 * @param {number} scaleUi
 */
export function mockupSunRadiusPx(scaleUi) {
  return Math.max(20, Math.round((MOCKUP_SUN_DIAMETER_PX / 2) * Number(scaleUi)));
}

/**
 * @param {number} scaleUi
 */
export function mockupMoonRadiusPx(scaleUi) {
  return Math.max(14, Math.round((MOCKUP_MOON_DIAMETER_PX / 2) * Number(scaleUi)));
}
